import { useEffect, useState, useCallback, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import useScrollLock from "../../hooks/useScrollLock";
import styles from "./FilePreview.module.css";

const PdfPreview = lazy(() => import("./PdfPreview"));

const ANIMATION_DURATION_MS = 200;

const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp", ".gif"];
const PDF_EXTENSIONS = [".pdf"];

function getFileType(file) {
  const mimeType = (file.mimeType || "").toLowerCase();

  if (mimeType.startsWith("image/")) {
    return "image";
  }
  if (mimeType === "application/pdf") {
    return "pdf";
  }

  const name = (file.name || "").toLowerCase();
  if (IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext))) {
    return "image";
  }
  if (PDF_EXTENSIONS.some((ext) => name.endsWith(ext))) {
    return "pdf";
  }

  return "unsupported";
}

function BackArrowIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M12.5 15L7.5 10L12.5 5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="shrink-0" aria-hidden="true">
      <path
        d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LoadingSkeleton() {
  return (
    <div className={styles.loadingSkeleton}>
      <div className={styles.spinner} />
      <span className={styles.loadingText}>Loading preview…</span>
    </div>
  );
}

function FilePreviewContent({ file, fileType }) {
  const url = file.downloadURL;

  if (!url) {
    return (
      <div className={styles.centerMessage}>
        <span className={styles.centerMessageText}>No preview available for this file.</span>
      </div>
    );
  }

  if (fileType === "image") {
    return (
      <div className={styles.imageWrapper}>
        <img src={url} alt={file.name} className={styles.previewImage} />
      </div>
    );
  }

  if (fileType === "pdf") {
    return (
      <Suspense fallback={<LoadingSkeleton />}>
        <PdfPreview url={url} />
      </Suspense>
    );
  }

  return <UnknownFilePreview file={file} />;
}

// Stored files often have no extension: show the file if it is an image,
// otherwise offer to open it.
function UnknownFilePreview({ file }) {
  const [isImage, setIsImage] = useState(true);

  if (isImage) {
    return (
      <div className={styles.imageWrapper}>
        <img
          src={file.downloadURL}
          alt={file.name}
          className={styles.previewImage}
          onError={() => setIsImage(false)}
        />
      </div>
    );
  }

  return (
    <div className={styles.centerMessage}>
      <span className={styles.centerMessageText}>
        Preview is not available for this file type.
      </span>
      <a
        href={file.downloadURL}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.openFileButton}
      >
        Open file
      </a>
    </div>
  );
}

export default function FilePreview({ file, onClose }) {
  const { lockScroll, unlockScroll } = useScrollLock();
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(true);

  const fileType = getFileType(file);

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(() => {
      setMounted(false);
      onClose();
    }, ANIMATION_DURATION_MS);
  }, [onClose]);

  useEffect(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setVisible(true));
    });
  }, []);

  useEffect(() => {
    lockScroll();
    return unlockScroll;
  }, [lockScroll, unlockScroll]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleClose]);

  if (!mounted || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className={`${styles.overlay} ${visible ? styles.overlayVisible : styles.overlayHidden}`}
      role="dialog"
      aria-modal="true"
    >
      <header className={styles.header}>
        <button
          type="button"
          aria-label="Back from preview"
          onClick={handleClose}
          className={styles.backButton}
        >
          <BackArrowIcon />
        </button>

        <div className={styles.titleSection}>
          {file.downloadURL ? (
            <a
              href={file.downloadURL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.titleLink}
              title="Open in new tab"
            >
              <span className={styles.titleText}>{file.name}</span>
              <ExternalLinkIcon />
            </a>
          ) : (
            <span className={styles.titleOnly}>{file.name}</span>
          )}
        </div>

        <div className={styles.headerSpacer} />
      </header>

      <div className={styles.contentArea} onClick={handleClose}>
        <div className={styles.contentInner} onClick={(event) => event.stopPropagation()}>
          <FilePreviewContent file={file} fileType={fileType} />
        </div>
      </div>
    </div>,
    document.body
  );
}
